import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Check, Loader2, Plus, Star, Timer, Trash2, Undo2, X } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const field = "h-11 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold";

const Stars = ({ value = 0, onPick, testId }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onPick} data-testid={onPick ? `${testId}-${n}` : undefined}
        onClick={() => onPick?.(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`}
        className={`transition-transform ${onPick ? "hover:scale-125" : "cursor-default"}`}>
        <Star className={`h-4 w-4 ${n <= Math.round(value) ? "fill-gold text-gold" : "text-ink-600"}`} />
      </button>
    ))}
  </span>
);

const Metric = ({ label, value }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="mt-0.5 text-sm text-slate-200">{value}</p>
  </div>
);

export default function Affiliates() {
  const { refresh } = useAuth();
  const [partners, setPartners] = useState([]);
  const [jobs, setJobs] = useState({ incoming: [], outgoing: [] });
  const [card, setCard] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [rating, setRating] = useState(null);

  const load = () => Promise.all([
    api.get("/partners").then(({ data }) => setPartners(data)),
    api.get("/affiliate/jobs").then(({ data }) => setJobs(data)),
    api.get("/affiliate/scorecard").then(({ data }) => setCard(data)),
  ]).catch((e) => setError(errMsg(e)));

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy("invite"); setError(""); setNotice("");
    try {
      const { data } = await api.post("/partners", form);
      setNotice(data.status === "active"
        ? `${data.partner_name} is on the platform — you can farm trips out to them right away.`
        : `${data.partner_name} was added. They will see your jobs once they create their operator account.`);
      setForm({ name: "", email: "", phone: "" });
      load();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(""); }
  };

  const decide = async (offer, decision) => {
    setBusy(`${offer.id}-${decision}`); setError(""); setNotice("");
    try {
      const { data } = await api.post(`/affiliate/jobs/${offer.id}/${decision}`);
      setNotice(decision === "accept"
        ? `Job accepted — it is now manifest #${data.manifest.number} on your dispatch board.`
        : "Job declined.");
      if (decision === "accept") await refresh();
      load();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(""); }
  };

  const waitingFor = (iso) => {
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 60) return `${mins} min ago`;
    if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
    return `${Math.round(mins / 1440)} d ago`;
  };

  const recall = async (offer) => {
    setBusy(`${offer.id}-recall`); setError(""); setNotice("");
    try {
      await api.post(`/affiliate/offers/${offer.id}/recall`);
      setNotice(`Offer to ${offer.partner_name || offer.partner_email} pulled back.`);
      load();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(""); }
  };

  const saveRating = async (offer, stars, note) => {
    setBusy(`${offer.id}-rate`); setError(""); setNotice("");
    try {
      await api.post(`/partners/${offer.partner_id}/rating`, { offer_id: offer.id, stars, note });
      setNotice(`Thanks — ${offer.partner_name || "your partner"} is rated ${stars}/5 on that job.`);
      setRating(null);
      load();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(""); }
  };

  const StatusTag = ({ status }) => (
    <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
      status === "accepted" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
        : status === "declined" ? "border-red-500/30 bg-red-500/10 text-red-300"
        : status === "recalled" || status === "closed" ? "border-slate-500/30 bg-slate-500/10 text-slate-400"
        : "border-gold/30 bg-gold/10 text-gold"}`}>
      {status === "closed" ? "taken elsewhere" : status}
    </span>
  );

  return (
    <div data-testid="affiliates-page" className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Network</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Affiliates & farm-out</h1>
        <p className="mt-2 text-sm text-slate-400">
          Hand overflow trips to operators you trust — offer one or several at once and the first to
          accept takes it. Passenger contact details are only released once a job is accepted.
        </p>
      </div>

      {notice && <p data-testid="affiliate-success" className="text-sm text-emerald-400">{notice}</p>}
      {error && <p data-testid="affiliate-error" className="text-sm text-red-400">{error}</p>}

      {card && (
        <div data-testid="affiliate-scorecard" className="rounded-xl border border-gold/25 bg-gold/5 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Your reputation in the network</p>
          <div className="mt-4 grid grid-cols-2 gap-5 sm:grid-cols-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Rating</p>
              <div className="mt-1 flex items-center gap-2">
                <Stars value={card.rating || 0} />
                <span data-testid="scorecard-rating" className="font-mono text-xs text-slate-300">
                  {card.rating ? `${card.rating}/5` : "unrated"}
                </span>
              </div>
            </div>
            <Metric label="Reviews" value={card.ratings_count} />
            <Metric label="Jobs offered to you" value={card.jobs_received} />
            <Metric label="You accept" value={card.accept_rate != null ? `${card.accept_rate}%` : "—"} />
          </div>
          {card.reviews?.length > 0 && (
            <div className="mt-5 space-y-2 border-t border-gold/15 pt-4">
              {card.reviews.map((r, i) => (
                <div key={`${r.at}-${i}`} data-testid={`scorecard-review-${i}`} className="flex flex-wrap items-center gap-2 text-xs text-slate-300">
                  <Stars value={r.stars} />
                  <span className="text-slate-400">{r.from}</span>
                  {r.manifest_number ? <span className="text-slate-600">#{r.manifest_number}</span> : null}
                  {r.note ? <span className="text-slate-400">“{r.note}”</span> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form onSubmit={invite} className="space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6">
        <h2 className="text-lg font-medium text-slate-100">Add a partner operator</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input data-testid="partner-name" required placeholder="Company name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
          <Input data-testid="partner-email" required type="email" placeholder="Owner email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
          <Input data-testid="partner-phone" placeholder="Phone" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} />
        </div>
        <Button data-testid="partner-save" type="submit" disabled={busy === "invite"} className="h-11 bg-gold px-8 text-ink-900 hover:bg-gold-light">
          {busy === "invite" ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="mr-2 h-4 w-4" /> Add partner</>)}
        </Button>
      </form>

      <div className="space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Your network</p>
        {partners.length === 0 && (
          <p data-testid="partners-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-6 text-sm text-slate-500">
            No partners yet. Add an operator you trust to start farming trips out.
          </p>
        )}
        {partners.map((p) => (
          <div key={p.id} data-testid={`partner-card-${p.id}`} className="rounded-xl border border-ink-600 bg-ink-800 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-base font-medium text-slate-100">
                  <Building2 className="h-4 w-4 text-gold" /> {p.partner_name} <StatusTag status={p.status} />
                </p>
                <p className="mt-1 truncate text-xs text-slate-500">{p.partner_email}{p.partner_phone ? ` · ${p.partner_phone}` : ""}</p>
              </div>
              <button data-testid={`partner-delete-${p.id}`} className="shrink-0 text-slate-500 hover:text-red-400"
                onClick={async () => { await api.delete(`/partners/${p.id}`); load(); }}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div data-testid={`partner-stats-${p.id}`} className="mt-4 grid grid-cols-2 gap-4 border-t border-ink-600 pt-4 sm:grid-cols-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Rating</p>
                <div className="mt-1 flex items-center gap-2">
                  <Stars value={p.rating || 0} />
                  <span className="font-mono text-[11px] text-slate-400">
                    {p.rating ? `${p.rating} · ${p.ratings_count}` : "new"}
                  </span>
                </div>
              </div>
              <Metric label="Jobs sent" value={p.jobs_sent ?? 0} />
              <Metric label="They accept" value={p.accept_rate != null ? `${p.accept_rate}%` : "—"} />
              <Metric label="Answers in"
                value={p.answer_minutes != null
                  ? (p.answer_minutes < 60 ? `${Math.round(p.answer_minutes)} min` : `${Math.round(p.answer_minutes / 60)} h`)
                  : "—"} />
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Job board · offers to you</p>
        {jobs.incoming.length === 0 && (
          <p data-testid="jobs-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-6 text-sm text-slate-500">
            No inbound jobs right now.
          </p>
        )}
        {jobs.incoming.map((o) => (
          <div key={o.id} data-testid={`job-card-${o.id}`} className="rounded-xl border border-ink-600 bg-ink-800 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-100">{o.from_company_name}</p>
              <div className="flex items-center gap-3">
                <span data-testid={`job-payout-${o.id}`} className="font-display text-lg text-emerald-300">{money(o.payout)}</span>
                <StatusTag status={o.status} />
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-300">
              {o.trip?.trip_date} {o.trip?.pickup_time} · {o.trip?.pickup?.address} → {o.trip?.dropoff?.address}
            </p>
            {o.broadcast && o.status === "offered" && (
              <p data-testid={`job-race-${o.id}`} className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber-300">
                <Timer className="h-3 w-3" /> Offered to several operators — first to accept keeps it
              </p>
            )}
            {o.trip?.flight_number && <p className="mt-1 text-xs text-slate-500">Flight {o.trip.flight_number}</p>}
            {o.note && <p className="mt-2 text-xs text-slate-400">“{o.note}”</p>}
            {o.status === "offered" ? (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button data-testid={`job-accept-${o.id}`} disabled={busy !== ""} onClick={() => decide(o, "accept")}
                  className="bg-emerald-500 text-ink-900 hover:bg-emerald-400">
                  {busy === `${o.id}-accept` ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Check className="mr-2 h-4 w-4" /> Accept job</>)}
                </Button>
                <Button data-testid={`job-decline-${o.id}`} disabled={busy !== ""} onClick={() => decide(o, "decline")}
                  variant="outline" className="border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10">
                  <X className="mr-2 h-4 w-4" /> Decline
                </Button>
              </div>
            ) : o.accepted_manifest_id ? (
              <Link to={`/app/manifests/${o.accepted_manifest_id}`} data-testid={`job-manifest-${o.id}`}
                className="mt-3 inline-block font-mono text-xs text-gold">Open the manifest →</Link>
            ) : null}
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Trips you sent out</p>
        {jobs.outgoing.length === 0 && (
          <p data-testid="outgoing-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-6 text-sm text-slate-500">
            You have not farmed out any trips yet — use “Farm out” on a manifest.
          </p>
        )}
        {jobs.outgoing.map((o) => (
          <div key={o.id} data-testid={`outgoing-card-${o.id}`} className="rounded-xl border border-ink-600 bg-ink-800 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-slate-100">
                  Manifest #{o.manifest_number} → {o.partner_name || o.partner_email}
                </p>
                <p className="mt-1 text-xs text-slate-500">{o.trip?.trip_date} {o.trip?.pickup_time} · payout {money(o.payout)}</p>
                {o.status === "offered" && (
                  <p className="mt-1 font-mono text-[10px] text-slate-500">Offered {waitingFor(o.created_at)}</p>
                )}
                {o.answer_minutes != null && o.status !== "offered" && (
                  <p className="mt-1 font-mono text-[10px] text-slate-500">
                    Answered in {o.answer_minutes < 60 ? `${Math.round(o.answer_minutes)} min` : `${Math.round(o.answer_minutes / 60)} h`}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <StatusTag status={o.status} />
                {o.status === "offered" && (
                  <Button data-testid={`job-recall-${o.id}`} size="sm" variant="outline" disabled={busy !== ""}
                    onClick={() => recall(o)}
                    className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
                    {busy === `${o.id}-recall` ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Undo2 className="mr-2 h-3.5 w-3.5" /> Pull back</>)}
                  </Button>
                )}
              </div>
            </div>

            {o.status === "accepted" && (
              <div className="mt-4 border-t border-ink-600 pt-4">
                {o.my_rating ? (
                  <div data-testid={`outgoing-rated-${o.id}`} className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">You rated them</span>
                    <Stars value={o.my_rating} />
                    {o.my_rating_note ? <span>“{o.my_rating_note}”</span> : null}
                    <button data-testid={`outgoing-rerate-${o.id}`} className="font-mono text-[10px] uppercase tracking-widest text-gold hover:underline"
                      onClick={() => setRating({ offer: o, stars: o.my_rating, note: o.my_rating_note || "" })}>
                      change
                    </button>
                  </div>
                ) : rating?.offer?.id === o.id ? null : (
                  <Button data-testid={`outgoing-rate-open-${o.id}`} size="sm" variant="outline"
                    className="border-gold/40 bg-transparent text-gold hover:bg-gold/10"
                    onClick={() => setRating({ offer: o, stars: 0, note: "" })}>
                    <Star className="mr-2 h-3.5 w-3.5" /> Rate this partner
                  </Button>
                )}
                {rating?.offer?.id === o.id && (
                  <div data-testid={`rating-panel-${o.id}`} className="mt-3 space-y-3 rounded-lg border border-gold/30 bg-gold/5 p-4">
                    <div className="flex items-center gap-3">
                      <Stars value={rating.stars} testId={`rating-star-${o.id}`}
                        onPick={(n) => setRating({ ...rating, stars: n })} />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                        {rating.stars ? `${rating.stars}/5` : "pick a score"}
                      </span>
                    </div>
                    <Input data-testid={`rating-note-${o.id}`} value={rating.note} maxLength={200}
                      placeholder="On time? Clean car? Anything to remember." className={field}
                      onChange={(e) => setRating({ ...rating, note: e.target.value })} />
                    <div className="flex gap-2">
                      <Button data-testid={`rating-save-${o.id}`} size="sm" disabled={!rating.stars || busy !== ""}
                        className="bg-gold text-ink-900 hover:bg-gold-light"
                        onClick={() => saveRating(o, rating.stars, rating.note)}>
                        {busy === `${o.id}-rate` ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save rating"}
                      </Button>
                      <Button data-testid={`rating-cancel-${o.id}`} size="sm" variant="outline"
                        className="border-ink-600 bg-transparent text-slate-300" onClick={() => setRating(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
